// src/services/predictiveFailureService.js
// Service for interacting with the predictive failure and asset health endpoints.
// --- UPDATE (ML Ops + performance) ---
// • getAllAssetHealth: the per-asset requests now run with bounded concurrency (default 6).
//   Promise.all over every asset fired hundreds of requests at once and could trip the API's
//   rate limits or the browser's connection pool, stalling the rest of the page.
// • triggerRulModelRetraining: a tracked ML control-plane run (champion/challenger, audit, live
//   broadcast). By default it waits for the outcome, which is what RulModelInsightsWidget shows.
// • getActivePredictiveAlerts: implemented (the file ended with its doc comment only).
// --- END UPDATE ---


import { authenticatedFetch } from './api'; // Corrected import
import * as AssetService from './assetService';
// --- UPDATE (ML Ops) ---
import mlOpsService from './mlOpsService';

const HEALTH_CONCURRENCY = 6;

/** Runs fn over items with at most `limit` requests in flight. */
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return out;
}
// --- END UPDATE ---


export const getAssetHealth = async (assetId) => {
  try {
    // authenticatedFetch is now correctly imported and used
    const response = await authenticatedFetch(`/predictive-failure/health/${assetId}`);
    return response; // Assuming the response is the data object
  } catch (error) {
    console.error(`Error fetching health for asset ${assetId}:`, error);
    return { asset_id: assetId, health_status: 'unknown' };
  }
};

export const getAllAssetHealth = async ({ concurrency = HEALTH_CONCURRENCY } = {}) => {
  try {
    const assets = await AssetService.fetchAssets();
    // --- UPDATE: fetchAssets may return { data: [...] } or an array ---
    const list = Array.isArray(assets) ? assets : (Array.isArray(assets?.data) ? assets.data : []);
    if (list.length === 0) {
      return {};
    }
    const healthResults = await mapLimit(list, Math.max(1, concurrency), (asset) => getAssetHealth(asset.id));
    // --- END UPDATE ---
    const healthMap = healthResults.reduce((acc, health) => {
      if (health && health.asset_id) {
        acc[health.asset_id] = health;
      }
      return acc;
    }, {});
    return healthMap;
  } catch (error) {
    console.error('Error fetching all asset health data:', error);
    throw error;
  }
};

export const runAnalysis = async () => {
  try {
    const response = await authenticatedFetch('/predictive-failure/run-analysis', { method: 'POST' });
    return response;
  } catch (error) {
    console.error('Error triggering predictive failure analysis:', error);
    throw error;
  }
};

/**
   * Bulk adds historical failure records.
   * @param {Array<object>} failures - An array of failure objects.
   * @returns {Promise<object>} The API response.
   */
  export const addHistoricalFailures = async (failures) => {
    try {
      const data = await authenticatedFetch('/predictive-failure/historical-failures', {
        method: 'POST',
        body: JSON.stringify({ failures }),
      });
      return data;
    } catch (error) {
      console.error('Error adding historical failures:', error);
      throw error;
    }
  };

/**
 * Triggers a retraining job for the predictive failure (RUL) model.
 * --- UPDATE (ML Ops): tracked control-plane run.
 * @param {{ wait?: boolean, onProgress?: Function, signal?: AbortSignal }} [options]
 * @returns {Promise<object>} { jobId, status, decision, model_version, message }
 */
export const triggerRulModelRetraining = async ({ wait = true, onProgress, signal } = {}) => {
    if (!wait) {
        const job = await mlOpsService.train('RUL_PREDICTION');
        return { jobId: job.id, status: 'running', message: job.alreadyActive ? 'The RUL model is already training.' : 'RUL model training started.', job };
    }
    return mlOpsService.trainAndWait('RUL_PREDICTION', { onProgress, signal });
};

/**
 * Fetches the active predictive failure alerts (assets at risk).
 * --- UPDATE: implemented — same endpoint as predictiveAlertService.fetchAssetsAtRisk.
 * @param {{ limit?: number }} [options]
 * @returns {Promise<{ success: boolean, data: Array, pagination?: object }>}
 */
export const getActivePredictiveAlerts = async ({ limit = 10 } = {}) => {
  return authenticatedFetch(`/predictive/alerts?isResolved=false&limit=${encodeURIComponent(limit)}`);
};
// --- END UPDATE ---
